import { tokens } from '../../../tokens'
import { SERIES_COLORS, ECHARTS_DECALS } from '../seriesStyles'

// Climate ECharts theme, generated from the design tokens so charts are visibly
// part of the Tellus suite (Inter type, ink / ink-muted text, drizzly-day
// gridlines) - never a stock ECharts look. Tuned from a sourced research pass
// (see docs/superpowers/specs/2026-07-04-charts-design-guidance.md): airy
// mid-tone blue fills, faint dashed gridlines, stripped axis chrome, small
// top-rounded bars, generous gaps. Light mode (dark mode is a spec non-goal).
const { color } = tokens

export const CLIMATE_ECHARTS_THEME = 'climate'

// Series colours + decal textures come from the ONE shared vocabulary
// (seriesStyles.ts) so ECharts and the CSS bars render every series identically:
// a Tellus blue paired with a distinct texture, told apart by both.
export const CLIMATE_CHART_COLORS = SERIES_COLORS

// Reserved emphasis / target-line / highlighted-series color.
export const TELLUS_EMPHASIS = '#002338' // fine-barrel dark

// Sequential blue ramp (light -> deep) for magnitude/heat encodings if needed.
export const TELLUS_BLUE_RAMP = ['#d1e1eb', '#99bdd3', '#74a5c3', '#0084d6', '#0067a5', '#004570']

export const CLIMATE_DECALS = ECHARTS_DECALS

export const CLIMATE_ARIA_DECAL = {
  enabled: true,
  decal: { show: true, decals: CLIMATE_DECALS },
}

// Number abbreviation for axis labels + tooltips (1.2k, 3.4M) with tabular feel.
export const abbreviateNumber = (value: number): string => {
  const abs = Math.abs(value)
  if (abs >= 1e9) return `${+(value / 1e9).toFixed(1)}B`
  if (abs >= 1e6) return `${+(value / 1e6).toFixed(1)}M`
  if (abs >= 1e3) return `${+(value / 1e3).toFixed(1)}k`
  return `${value}`
}

const AXIS_LABEL = color.inkMuted // #6f8998 - soft slate, matches app muted text
const GRIDLINE = color.border // #ccd9e3 - drizzly-day, matches card borders

export const climateThemeObject = {
  color: CLIMATE_CHART_COLORS,
  backgroundColor: 'transparent',
  textStyle: {
    fontFamily: 'Inter var, Inter, ui-sans-serif, system-ui, sans-serif',
    color: color.ink,
  },
  title: {
    left: 'left',
    textStyle: { color: color.ink, fontSize: 15, fontWeight: 600 },
    subtextStyle: { color: AXIS_LABEL, fontSize: 12 },
  },
  legend: {
    bottom: 0,
    icon: 'roundRect',
    itemWidth: 10,
    itemHeight: 10,
    itemGap: 18,
    textStyle: { color: AXIS_LABEL, fontSize: 12 },
  },
  grid: { top: 44, left: 16, right: 24, bottom: 8, containLabel: true },
  categoryAxis: {
    // Strip the chrome - bars/columns float on whitespace.
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: AXIS_LABEL, fontSize: 12, margin: 12 },
    splitLine: { show: false },
    splitArea: { show: false },
  },
  valueAxis: {
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: AXIS_LABEL, fontSize: 12 },
    // Only faint dashed gridlines, in the card-border token.
    splitLine: { show: true, lineStyle: { color: GRIDLINE, type: 'dashed', width: 1 } },
    splitArea: { show: false },
  },
  tooltip: {
    // Light card matching GridCard/overlays: borderless, soft shadow, card radius.
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderWidth: 0,
    padding: [10, 14],
    extraCssText: 'box-shadow:0 6px 24px rgba(5,28,44,0.12);border-radius:8px;',
    textStyle: { color: color.ink, fontSize: 12 },
    axisPointer: {
      type: 'shadow',
      shadowStyle: { color: 'rgba(0,103,165,0.06)' },
      lineStyle: { color: GRIDLINE, width: 1, type: 'dashed' },
    },
  },
  bar: {
    // Rounded blocks with a small white gap between stacked segments (the
    // reference look): every segment gets generous all-corner rounding.
    itemStyle: { borderRadius: 6, borderColor: '#ffffff', borderWidth: 3 },
    barCategoryGap: '42%',
  },
  line: {
    lineStyle: { width: 2.5 },
    symbol: 'circle',
    symbolSize: 7,
    showSymbol: false,
    smooth: false,
  },
}
